import { z } from "zod";

/**
 * A ROLE'S RESPONSIBILITIES — its responsibilities, in the author's own words.
 *
 * ══════════════════════════════════════════════════════════════════════════
 * THIS IS THE FIRST PLACE THIS PRODUCT HAS EVER STORED ONE.
 * ══════════════════════════════════════════════════════════════════════════
 *
 * The New role wizard's step 2 said so out loud, in its own copy — "Responsibilities are
 * not something a role stores yet" — and that sentence was TRUE rather than a
 * bug: no column, no endpoint and no contract existed anywhere in the monorepo.
 * `HeldStep.tsx` and `newRoleDraft.ts` both carried the reasoning for asking
 * nothing rather than asking and discarding, and that reasoning was right for
 * as long as there was nowhere to put the answer. This file, `RoleResponsibility`
 * and the three routes that reach it are what removes the premise.
 *
 * ── ONE SENTENCE PER ROW, ORDERED, NOT A BLOB ──────────────────────────────
 *
 * `Role.jobDescription` already holds one line of prose about what a Role does.
 * Responsibilities are a LIST and are addressed individually — the step adds
 * them one at a time and removes them one at a time — so a second free-text
 * column would have made "remove the third responsibility" a string operation on the
 * client and unorderable on the server.
 *
 * ══════════════════════════════════════════════════════════════════════════
 * PER-ROW CRUD WITH STABLE IDS — AND THIS DEPARTS FROM THE SCOPE MODEL ON
 * PURPOSE. READ THIS BEFORE "MAKING IT CONSISTENT".
 * ══════════════════════════════════════════════════════════════════════════
 *
 * `RoleScopeLine` and `RoleVariable` are both written by a whole-array `PUT`
 * that deletes every row and re-inserts, and their own contracts warn that ROW
 * IDS DO NOT SURVIVE A SAVE. That shape is right for them and wrong here, for
 * two reasons that do not apply to either of them:
 *
 * 1. 🚨 **THE PRODUCT ALREADY PROMISES THESE ROWS ARE REFERENCEABLE.** Step 2's
 *    shipped body text reads "Tasks link back to these, and the role's map is
 *    judged against them". A responsibility whose `id` changes on every save cannot be
 *    the target of that link — the foreign key would break on the next edit of
 *    an unrelated responsibility. Choosing the destructive shape tonight would make the
 *    sentence on the screen unimplementable later, quietly.
 *
 * 2. **A whole-array PUT cannot distinguish success from total data loss.** A
 *    caller that sends one element because it only knew about one gets `200`
 *    and a one-element view back, which is exactly what a correct single-responsibility
 *    Role returns. `DELETE` naming one row has no such reading: there is no
 *    request here whose success looks like the destruction of everything else.
 *
 * What this costs, and it is the honest trade: the wizard's flush is N
 * sequential `POST`s rather than one `PUT`, matching `addTeam`'s existing shape
 * in `useCreateRoleFromWizard.ts`. Round trips are the thing being spent, and
 * they are cheaper than a reference that cannot be made.
 *
 * ── THERE IS NO REORDER ENDPOINT, AND THAT IS A STATED LIMIT ───────────────
 *
 * `position` is assigned by the server at the END of the list on every add. The
 * list can be added to, read in order and removed from; it cannot be
 * rearranged.
 *
 * debt: responsibilities are append-ordered and cannot be reordered — a responsibility
 *       written third stays third until it is deleted and re-added.
 *       Upgrade trigger: the first screen that lets an author drag one, OR any
 *       consumer that reads `position` as a priority rather than as an
 *       insertion order.
 */

/**
 * The longest a single responsibility may be.
 *
 * 🚨 ONE DEFINITION, AND IT DRIVES ALL THREE LAYERS. The Zod bound below is
 * built from it, both editors use it as their `maxLength`, and the database's
 * `RoleResponsibility_text_shape` restates it in SQL. It was briefly typed as a
 * literal `500` in three places; nothing gated the drift, and the failure is
 * silent in the expensive direction — a client cap ABOVE the contract's produces
 * a 400 the author cannot see coming, and one BELOW it forbids something the
 * product allows.
 *
 * The SQL copy is the one that cannot import this. Changing this number means
 * changing that CHECK in a new migration, in the same commit.
 */
export const ROLE_RESPONSIBILITY_MAX_LENGTH = 500;

/**
 * One responsibility, as a caller authors it.
 *
 * NO `id` AND NO `position`. The id is the server's — it is the whole point of
 * this shape — and `position` is assigned at the end of the list on the way in,
 * so a client cannot claim a slot or leave a hole.
 */
export const RoleResponsibilityInputSchema = z.strictObject({
  /**
   * The responsibility itself — "Screen every inbound application within one working
   * day", "Answer billing disputes under 200 euros without escalating".
   *
   * 🚨 NON-BLANK IS ENFORCED HERE AND IN THE DATABASE, AND THE TWO ARE NOT
   * REDUNDANT. `RoleResponsibility_text_shape` refuses a blank row even if this
   * contract were removed; this refuses it with a message the author can act on
   * rather than a `500` carrying a constraint name. `trim()` runs BEFORE
   * `min(1)`, so a row of spaces is rejected rather than stored as a responsibility that
   * renders as an empty line nobody can select.
   *
   * The cap restates `RoleResponsibility_text_shape` rather than inventing a
   * second bound. A responsibility is a sentence; something longer is a job description
   * and `Role.jobDescription` is where that goes.
   */
  text: z.string().trim().min(1).max(ROLE_RESPONSIBILITY_MAX_LENGTH)
});

/** One stored responsibility, as the API returns it. */
export const RoleResponsibilityViewSchema = RoleResponsibilityInputSchema.extend({
  /**
   * Stable across edits of every other responsibility, and stable across edits of this
   * one's text. This is the field the whole shape exists to provide.
   */
  id: z.string(),
  /** Its place in the list, `0`-based. The read is ordered by it. */
  position: z.number(),
  updatedAt: z.string()
});

/** A Role's whole set of responsibilities, in read order. */
export const RoleResponsibilitiesViewSchema = z.strictObject({
  responsibilities: z.array(RoleResponsibilityViewSchema)
});

/**
 * The path segments of a route that names ONE responsibility of one Role.
 *
 * BOTH SEGMENTS ARE CARRIED, and the `:id` is not decoration: a
 * `responsibilityId`-only route would happily accept a responsibility belonging to a
 * DIFFERENT Role of the same organisation and delete it, which is the case this
 * pair refuses in the adapter's three-column predicate.
 *
 * 🚨 BOTH ARE `.uuid()`, AND THAT WAS ONLY EVER AVAILABLE AS ONE MOVE.
 * `id-validation-consistency.test.ts` refuses a PARTIAL tightening: an id name
 * that is uuid-checked in one request object of a manifest must be uuid-checked
 * in all of them, and `RolePathVarsSchema` is shared by every `/api/roles/:id/*`
 * route — so `.uuid()` here alone would have flagged every one of its siblings.
 * NEX-3552 tightened the whole Roles surface at once, which is the only spelling
 * that gate accepts; `RoleSystemImpactPathVarsSchema` records the same ruling.
 *
 * ⚠️ THE USE CASE STILL SHAPE-CHECKS `responsibilityId`, AND THAT IS A FLOOR
 * RATHER THAN A DUPLICATE. `RoleResponsibility.id` is `@db.Uuid`, so a value
 * that reached Prisma unchecked raises `P2023` and renders as a flat 400 naming
 * no field. `RemoveRoleResponsibilityUseCase` answers 404 instead, exactly as
 * `AssertRoleInOrganizationService` already does for `roleId`, so malformed,
 * absent and foreign stay one answer for any caller that does not arrive
 * through this schema.
 */
export const RoleResponsibilityPathVarsSchema = z.strictObject({
  id: z.string().uuid(),
  responsibilityId: z.string().uuid()
});

/** One responsibility, as authored. */
export type RoleResponsibilityInput = z.infer<typeof RoleResponsibilityInputSchema>;

/** One stored responsibility. */
export type RoleResponsibilityView = z.infer<typeof RoleResponsibilityViewSchema>;

/** A Role's whole set of responsibilities, as stored. */
export type RoleResponsibilitiesView = z.infer<typeof RoleResponsibilitiesViewSchema>;

/** The two ids a single-responsibility route carries. */
export type RoleResponsibilityPathVars = z.infer<typeof RoleResponsibilityPathVarsSchema>;
